import { CurrenciesIcon } from '@/features/earn/components';
import { CurrencyIconVariant } from '@/features/earn/components/CurrenciesIcon';
import { keyGenerator } from '@/utils/keyGenerator';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { ScrollView, styled, SxProp, View } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { TextBody, TextSmall } from '../typography';
import { WalletsData } from './useAccountSelector';

const PressableView = styled(Pressable)();

interface AccountSelectorDropdownMenuProps {
  hasFocus: boolean;
  displayWalletData: WalletsData;
  changeFocus: (value: boolean) => void;
  getDecimalsByAsset: (asset: string) => number;
  selectWalletId: (id: string) => void;
  onValueChange: (value: string) => void;
  dropdownSx?: SxProp;
}

export const AccountSelectorDropdownMenu: React.FC<AccountSelectorDropdownMenuProps> = ({
  hasFocus,
  displayWalletData,
  changeFocus,
  getDecimalsByAsset,
  selectWalletId,
  onValueChange,
  dropdownSx
}) => {
  if (!hasFocus) return null;

  const { t } = useTranslation('earn');

  return (
    <View sx={{...styles.dropdownMenu,...dropdownSx}}>
      <ScrollView>
        {displayWalletData.length === 0 ? (
          <View sx={{ alignItems: 'center', paddingX: '$3' }}>
            <TextBody>{t('earn-invest::glossary.search-no-result')}</TextBody>
          </View>
        ) : null}
        {displayWalletData.length > 0
          ? displayWalletData.map((item) => (
              <PressableView
                key={keyGenerator('account-selector')}
                onPress={() => {
                  selectWalletId(item.value);
                  changeFocus(false);
                  onValueChange(item.value);
                }}>
                {({ hovered }) => (
                  <View sx={{ ...styles.dropdownItem, backgroundColor: hovered ? '$backgroundDropdownActive' : '$backgroundPopup' }}>
                    <CurrenciesIcon asset={item.asset} variant={CurrencyIconVariant.dropdown} hideText />
                    <View sx={{ flex: 1 }}>
                      <TextBody label>{item.label}</TextBody>
                      <TextSmall secondary>{item.accountNumber}</TextSmall>
                    </View>
                    <TextBody>
                      {formatAmountByDecimals({
                        value: item.balance,
                        decimals: getDecimalsByAsset(item.asset),
                        suffix: item.asset
                      })}
                    </TextBody>
                  </View>
                )}
              </PressableView>
            ))
          : null}
      </ScrollView>
    </View>
  );
};

enum StyleName {
  dropdownMenu = 'dropdownMenu',
  dropdownItem = 'dropdownItem'
}

const styles: Record<StyleName, SxProp> = {
  dropdownMenu: {
    position: 'absolute',
    top: '$15',
    left: 0,
    width: '100%',
    maxHeight: 250,
    paddingY: '$2',
    backgroundColor: '$backgroundPopup',
    borderRadius: 5,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10
  },
  dropdownItem: {
    paddingX: '$4',
    paddingY: '$3',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$3'
  }
};
